import {Trans} from '@lingui/macro';
import React from 'react';

import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {makeEnumTypeGuard} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  VirtualTableColumnCellsEnum,
  virtualTableRowCheckboxWrapperWidth,
  virtualTableRowOffset,
  VirtualTableSortableColumnDirectionsEnum,
} from '../../../components/common/table/helpers/tableHelpers';
import {
  SelectedColumnSort,
  SortableTableColumn,
  TableColumn,
} from '../../../components/common/table/tableColumn';
import {CompanyModel} from '../../../models/api/companyModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {RuleBundleModelStrict} from '../../../models/api/ruleBundleModel';
import {RuleBundleTypesEnum} from '../../../models/api/ruleCommonTypes';
import {RuleBaseModelStrict} from '../../../models/api/ruleModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsRulesUrl} from '../../../models/url/settingsUrlModel';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {PaginationParameter} from '../../helpers/resourceListPagination/resourceListPaginationOffset';
import {buildRuleDescription} from '../computeRuleRecipeDescription';
import {RuleParametersDependencies} from '../ruleConditionModel';
import {getRuleType, RuleBundleCategoriesEnum, SettingsRuleListFiltersState} from './ruleBundleCategory';

export enum RuleListTableColumnsEnum {
  NAME = 'name',
  ORDER = 'order',
  ACTIVE = 'active',
  LAST_APPLIED = 'last_triggered_at',
}

export const RULE_LIST_COLUMNS: ReadonlyArray<TableColumn<RuleListTableColumnsEnum>> = [
  {
    id: RuleListTableColumnsEnum.NAME,
    title: <Trans id="rule-list-table-column-name">Name</Trans>,
    type: RuleListTableColumnsEnum.NAME,
    cellType: VirtualTableColumnCellsEnum.TEXT,
    settings: {
      cellWidth: {
        shouldFillRemainingWidth: true,
        minWidth: 330 - virtualTableRowOffset,
      },
      sticky: {
        isSticky: true,
        stickyOffset: virtualTableRowCheckboxWrapperWidth,
      },
    },
  },
  {
    id: RuleListTableColumnsEnum.ORDER,
    title: <Trans id="rule-list-table-column-order">Order</Trans>,
    type: RuleListTableColumnsEnum.ORDER,
    cellType: VirtualTableColumnCellsEnum.NUMBER,
    settings: {
      cellWidth: {
        width: 98,
      },
    },
  },
  {
    id: RuleListTableColumnsEnum.ACTIVE,
    title: <Trans id="rule-list-table-column-active">Active</Trans>,
    type: RuleListTableColumnsEnum.ACTIVE,
    cellType: VirtualTableColumnCellsEnum.HANDLE,
    settings: {
      cellWidth: {
        width: 80,
      },
    },
  },
  {
    id: RuleListTableColumnsEnum.LAST_APPLIED,
    title: <Trans id="rule-list-table-column-last-applied">Last applied</Trans>,
    type: RuleListTableColumnsEnum.LAST_APPLIED,
    cellType: VirtualTableColumnCellsEnum.DATE,
    settings: {
      cellWidth: {
        width: 130,
      },
    },
  },
];

export const SORTABLE_COLUMNS: ReadonlyArray<SortableTableColumn> = [
  {
    id: RuleListTableColumnsEnum.NAME,
    directions: [
      VirtualTableSortableColumnDirectionsEnum.ASCENDING,
      VirtualTableSortableColumnDirectionsEnum.DESCENDING,
    ],
  },
  {
    id: RuleListTableColumnsEnum.ORDER,
    directions: [
      VirtualTableSortableColumnDirectionsEnum.ASCENDING,
      VirtualTableSortableColumnDirectionsEnum.DESCENDING,
    ],
  },
  {
    id: RuleListTableColumnsEnum.ACTIVE,
    directions: [
      VirtualTableSortableColumnDirectionsEnum.ASCENDING,
      VirtualTableSortableColumnDirectionsEnum.DESCENDING,
    ],
  },
  {
    id: RuleListTableColumnsEnum.LAST_APPLIED,
    directions: [
      VirtualTableSortableColumnDirectionsEnum.ASCENDING,
      VirtualTableSortableColumnDirectionsEnum.DESCENDING,
    ],
  },
];

export enum RuleListTableSortsEnum {
  NAME_ASC = 'name',
  NAME_DESC = '-name',
  ORDER_ASC = 'order',
  ORDER_DESC = '-order',
  ACTIVE_ASC = 'active',
  ACTIVE_DESC = '-active',
  LAST_APPLIED_ASC = 'last_triggered_at',
  LAST_APPLIED_DESC = '-last_triggered_at',
}

export const isRuleListTableSortEnum = makeEnumTypeGuard(RuleListTableSortsEnum);

export function computeSortedColumn(
  sort: RuleListTableSortsEnum | undefined,
): SelectedColumnSort | undefined {
  if (!sort) {
    return undefined;
  }

  const direction = sort.startsWith('-')
    ? VirtualTableSortableColumnDirectionsEnum.DESCENDING
    : VirtualTableSortableColumnDirectionsEnum.ASCENDING;

  switch (sort) {
    case RuleListTableSortsEnum.NAME_ASC:
    case RuleListTableSortsEnum.NAME_DESC:
      return {
        id: RuleListTableColumnsEnum.NAME,
        direction,
      };
    case RuleListTableSortsEnum.ORDER_ASC:
    case RuleListTableSortsEnum.ORDER_DESC:
      return {
        id: RuleListTableColumnsEnum.ORDER,
        direction,
      };
    case RuleListTableSortsEnum.ACTIVE_ASC:
    case RuleListTableSortsEnum.ACTIVE_DESC:
      return {
        id: RuleListTableColumnsEnum.ACTIVE,
        direction,
      };
    case RuleListTableSortsEnum.LAST_APPLIED_ASC:
    case RuleListTableSortsEnum.LAST_APPLIED_DESC:
      return {
        id: RuleListTableColumnsEnum.LAST_APPLIED,
        direction,
      };
    default:
      throw new UnreachableError(sort);
  }
}

export interface RuleListItem {
  id: number;
  name: string;
  type: RuleBundleCategoriesEnum | null;
  description: string;
  namespace: string;
  order: number;
  isActive: boolean;
  impact: InboxModel[];
  lastApplied: number | undefined;
  createdAt: number | undefined;
  bundleType: string | undefined;
  firstRuleId: number;
}

function buildRuleType(model: RuleBundleModelStrict) {
  const {type, rules} = model;
  const [firstRule] = rules;

  switch (type) {
    case RuleBundleTypesEnum.SLA:
      return RuleBundleCategoriesEnum.SLA;

    case RuleBundleTypesEnum.CSAT:
      return RuleBundleCategoriesEnum.REPLY;

    case RuleBundleTypesEnum.REQUIRED_TAGGING:
      return RuleBundleCategoriesEnum.TAG;

    case RuleBundleTypesEnum.SINGLE:
      return firstRule ? getRuleType(firstRule) : null;

    default:
      return null;
  }
}

function getLastApplied(rules: ReadonlyArray<RuleBaseModelStrict>): number | undefined {
  const rulesLastTriggeredAt = rules.map((rule) => rule.lastTriggeredAt).filter(isDefined);
  return rulesLastTriggeredAt.length > 0 ? Math.max(...rulesLastTriggeredAt) : undefined;
}

function getCreatedAt(rules: ReadonlyArray<RuleBaseModelStrict>): number | undefined {
  const rulesLastCreatedAt = rules.map((rule) => rule.createdAt).filter(isDefined);
  return rulesLastCreatedAt.length > 0 ? Math.max(...rulesLastCreatedAt) : undefined;
}

export function buildRuleListItem(
  model: RuleBundleModelStrict,
  dependencies: RuleParametersDependencies,
  localize: Localizer,
): RuleListItem | undefined {
  const {id, type: bundleType, rules, name, namespace} = model;
  const ruleModels = rules.map((rule) => ({...rule, bundle: model}));
  const type = buildRuleType(model);
  const firstRuleId = rules[0]?.id;

  return id && firstRuleId
    ? {
        id,
        name,
        type,
        description: buildRuleDescription(bundleType, ruleModels, dependencies, localize) ?? '',
        namespace,
        order: model.sortOrder ?? 0,
        isActive: rules.every((rule) => rule.active),
        impact: [],
        lastApplied: getLastApplied(rules),
        createdAt: getCreatedAt(rules),
        bundleType,
        firstRuleId,
      }
    : undefined;
}

/*
 * URL Helpers.
 */

function buildBasePath(company: CompanyModel, namespace: string | undefined) {
  return namespace ? `${company.url}/namespaces/${namespace}` : company.url;
}

export function buildRuleSearchUrl(
  company: CompanyModel,
  namespace: string | undefined,
  searchState: SettingsRuleListFiltersState,
  pagination: PaginationParameter,
  sortOrder: RuleListTableSortsEnum,
) {
  const path = `${buildBasePath(company, namespace)}/search_rule_bundle`;
  const {query = '', ...options} = searchState;
  return addQueryToPath(`${path}/${encodeURIComponent(query.toLowerCase())}`, {
    ...serializeSearchFiltersState(options),
    ...pagination,
    sort: sortOrder,
  });
}

export function buildRuleBundleBulkUrl(company: CompanyModel, namespace: string | undefined) {
  return `${buildBasePath(company, namespace)}/rule_bundles_bulk`;
}

export function buildRuleListEditUrl(currentUrl: SettingsRulesUrl, ruleItem: RuleListItem) {
  return buildSettingsPath({
    ...currentUrl,
    resourcePage: SettingsResourcePagesEnum.EDIT,
    namespace: ruleItem.namespace,
    bundleId: ruleItem.id,
    recipe: ruleItem.bundleType,
    ruleId: ruleItem.firstRuleId,
  });
}

export function buildRuleListNewUrl(currentUrl: SettingsRulesUrl) {
  return buildSettingsPath({
    ...currentUrl,
    resourcePage: SettingsResourcePagesEnum.NEW,
  });
}

function serializeSearchFiltersState(filtersState: SettingsRuleListFiltersState) {
  const categories =
    filtersState.categories && filtersState.categories.length > 0
      ? filtersState.categories.join(',')
      : undefined;

  const smart = filtersState.onlySmart || undefined;
  const inboxIds = filtersState.inbox ? `${filtersState.inbox}` : undefined;

  return {categories, smart, inbox_ids: inboxIds};
}
